import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, FileText, Users, Receipt, Settings, BarChart3, CreditCard, Package, BookOpen, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface CommandItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: () => void;
  category: string;
  keywords?: string[];
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (hash: string) => void;
}

export default function CommandPalette({ open, onClose, onNavigate }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const commands: CommandItem[] = useMemo(() => [
    // Navigation
    { id: 'dashboard', label: 'Dashboard', icon: <BarChart3 className="w-4 h-4" />, action: () => onNavigate('#dashboard'), category: 'Navigation', keywords: ['home', 'overview'] },
    { id: 'customers', label: 'Customers', icon: <Users className="w-4 h-4" />, action: () => onNavigate('#ar-customers'), category: 'Navigation', keywords: ['clients', 'contacts'] },
    { id: 'invoices', label: 'Invoices', icon: <FileText className="w-4 h-4" />, action: () => onNavigate('#invoicing'), category: 'Navigation', keywords: ['bills', 'billing'] },
    { id: 'payments', label: 'Payments', icon: <CreditCard className="w-4 h-4" />, action: () => onNavigate('#ar-payments'), category: 'Navigation', keywords: ['money', 'receive'] },
    { id: 'vendors', label: 'Vendors', icon: <Package className="w-4 h-4" />, action: () => onNavigate('#vendors'), category: 'Navigation', keywords: ['suppliers'] },
    { id: 'expenses', label: 'Expenses', icon: <Receipt className="w-4 h-4" />, action: () => onNavigate('#expenses'), category: 'Navigation', keywords: ['costs', 'spending'] },
    { id: 'accounts', label: 'Chart of Accounts', icon: <BookOpen className="w-4 h-4" />, action: () => onNavigate('#chart-of-accounts'), category: 'Navigation', keywords: ['ledger', 'GL'] },
    { id: 'journal', label: 'Journal Entries', icon: <BookOpen className="w-4 h-4" />, action: () => onNavigate('#journal-entries'), category: 'Navigation', keywords: ['entries', 'ledger'] },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" />, action: () => onNavigate('#settings-main'), category: 'Navigation', keywords: ['preferences', 'config'] },
    { id: 'reports', label: 'Reports', icon: <BarChart3 className="w-4 h-4" />, action: () => onNavigate('#reports'), category: 'Navigation', keywords: ['analytics', 'financial'] },
    // Actions
    { id: 'new-invoice', label: 'Create New Invoice', icon: <FileText className="w-4 h-4" />, action: () => onNavigate('#invoicing'), category: 'Actions', keywords: ['add', 'create'] },
    { id: 'new-customer', label: 'Add New Customer', icon: <Users className="w-4 h-4" />, action: () => onNavigate('#ar-customers'), category: 'Actions', keywords: ['add', 'create'] },
    { id: 'new-expense', label: 'Record Expense', icon: <Receipt className="w-4 h-4" />, action: () => onNavigate('#expenses'), category: 'Actions', keywords: ['add', 'create'] },
  ], [onNavigate]);

  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter(cmd =>
      cmd.label.toLowerCase().includes(q) ||
      cmd.category.toLowerCase().includes(q) ||
      cmd.keywords?.some(k => k.includes(q))
    );
  }, [query, commands]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      filtered[selectedIndex].action();
      onClose();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]"
        onClick={onClose}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm" />

        {/* Palette */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.15 }}
          onClick={e => e.stopPropagation()}
          className="relative w-full max-w-lg mx-4 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
            <Search className="w-5 h-5 text-slate-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search commands, pages..."
              className="flex-1 bg-transparent text-sm text-slate-900 dark:text-white placeholder:text-slate-400 outline-none"
            />
            <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono text-slate-400 bg-slate-100 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-80 overflow-y-auto py-2">
            {filtered.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-slate-400">
                No results for "{query}"
              </div>
            )}
            {['Navigation', 'Actions'].map(category => {
              const items = filtered.filter(c => c.category === category);
              if (items.length === 0) return null;
              return (
                <div key={category}>
                  <div className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">{category}</div>
                  {items.map((cmd, i) => {
                    const globalIndex = filtered.indexOf(cmd);
                    return (
                      <button
                        key={cmd.id}
                        onClick={() => { cmd.action(); onClose(); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                          globalIndex === selectedIndex
                            ? 'bg-primary/10 text-primary'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                      >
                        <span className={globalIndex === selectedIndex ? 'text-primary' : 'text-slate-400'}>{cmd.icon}</span>
                        <span className="flex-1 text-sm font-medium">{cmd.label}</span>
                        {globalIndex === selectedIndex && <ArrowRight className="w-3.5 h-3.5 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-800 flex items-center gap-4 text-[10px] text-slate-400">
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[9px]">↑↓</kbd> Navigate</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[9px]">↵</kbd> Select</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[9px]">Esc</kbd> Close</span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
